import { decryptBlob, fromBase64, CryptoError } from "../lib/blob/crypto";
import { BlobFormatError, decodeBlob, type UsageBlob } from "../lib/blob/format";
import { mergeBlobs, type MergedSnapshot } from "../lib/blob/merge";
import type { Settings } from "./store";

/**
 * Fetch every published blob, decrypt it here, and merge.
 *
 * The Worker only ever hands over ciphertext; the key exists in this browser and
 * nowhere else. Everything below runs client-side for that reason.
 */

/** A blob that arrived but could not be used, and why. */
export interface BlobFailure {
  machine: string;
  year: number;
  reason: string;
}

export interface LoadResult {
  snapshot: MergedSnapshot;
  /** Blobs that failed to decrypt or parse. Rendered, never swallowed. */
  failures: BlobFailure[];
  /** How many blobs the drop held, including the failures. */
  fetched: number;
}

export class LoadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LoadError";
  }
}

interface BlobRow {
  machine: string;
  year: number;
  bytes: number;
  updatedAt: number;
  ciphertext: string;
}

const FETCH_TIMEOUT_MS = 20_000;

/**
 * Load and decrypt everything in the drop.
 *
 * **Each blob is isolated.** One machine publishing something this build can't
 * read — a newer format, a different passphrase, a truncated write — must not
 * blank the whole dashboard; the machines that *did* decrypt still render, and
 * the failure is reported alongside them. A reader that throws on the first bad
 * blob turns one machine's problem into total blindness, which is the opposite
 * of what a monitoring dashboard is for.
 */
export async function loadSnapshot(settings: Settings): Promise<LoadResult> {
  const base = settings.dropUrl.trim().replace(/\/+$/, "");
  const rows = await fetchBlobs(base, settings.readToken.trim());

  const blobs: UsageBlob[] = [];
  const failures: BlobFailure[] = [];

  for (const row of rows) {
    try {
      const plain = await decryptBlob(fromBase64(row.ciphertext), settings.passphrase);
      blobs.push(await decodeBlob(plain));
    } catch (err) {
      failures.push({
        machine: row.machine,
        year: row.year,
        // Crypto and format errors already read as explanations; anything else
        // is unexpected and shown verbatim rather than flattened to "failed".
        reason:
          err instanceof CryptoError || err instanceof BlobFormatError
            ? err.message
            : `unexpected error: ${(err as Error).message}`,
      });
    }
  }

  // Every blob failing is worth calling out as its own condition: it almost
  // always means one wrong passphrase, not several independent problems.
  if (blobs.length === 0 && failures.length > 0) {
    throw new LoadError(
      failures.length === 1
        ? failures[0].reason
        : `none of the ${failures.length} stored payloads could be read — check the passphrase`,
    );
  }

  return { snapshot: mergeBlobs(blobs), failures, fetched: rows.length };
}

async function fetchBlobs(base: string, token: string): Promise<BlobRow[]> {
  if (!base) throw new LoadError("no dead-drop URL configured");

  let res: Response;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    res = await fetch(`${base}/v1/blobs`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: ctrl.signal,
    });
  } catch (err) {
    // The three realistic causes look identical to `fetch`, so name them rather
    // than surfacing "Failed to fetch", which sends people to the wrong place.
    throw new LoadError(
      (err as Error).name === "AbortError"
        ? `no response from ${base} within ${FETCH_TIMEOUT_MS / 1000}s`
        : `could not reach ${base} — wrong URL, offline, or blocked by the network`,
    );
  } finally {
    clearTimeout(timer);
  }

  if (res.status === 401) throw new LoadError("read token rejected by the dead drop");
  if (!res.ok) throw new LoadError(`dead drop returned HTTP ${res.status}`);

  const body = (await res.json()) as { ok?: boolean; blobs?: BlobRow[] };
  if (!body.ok || !Array.isArray(body.blobs)) {
    throw new LoadError("dead drop returned an unexpected response");
  }
  return body.blobs;
}
